import { Button, Dropdown, Modal, Space, Tooltip, message } from "antd";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import type { UserInputMode } from "../../hooks/useUserProfileCard";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import EditModeActions from "./EditModeActions";
import {
  demoteFromCompanyAdmin,
  promoteToCompanyAdmin,
} from "../../repository/data.repository";
import { getProblemMessage } from "../../utils/http";

export interface UserProfileActionsProps {
  readonly canEdit: boolean;
  readonly editor: {
    editing: boolean;
    saving: boolean;
    saveError: boolean;
    startEditing: () => void;
    cancelEditing: () => void;
    save: () => void;
  };
  readonly item: Record<string, any> | null;
  readonly onInputOpen: (mode: UserInputMode) => void;
  /** Tier-specific actions to surface beyond the universal Edit + Reset Password. */
  readonly show?: {
    assignReward?: boolean;
    confirmContacts?: boolean; // confirm email / phone / company phone (corporate)
    lock?: boolean; // lock / unlock (affiliate)
    // Audit fix 4 (2026-11-04) — Promote/Demote Company Admin. Only shown on
    // the Users page where the operator looks at a company user.
    companyAdminToggle?: boolean;
  };
  // Audit fix 4 — refresh the card after a promote/demote so the badge/role
  // name updates without a full page reload.
  readonly onReload?: () => void;
}

/**
 * The shared header actions for a user card: the Edit-mode Save/Cancel block, or the
 * "Schedule Event" primary + an action dropdown composed by tier. Each action is gated by its own
 * permission (not the global canEdit), so e.g. "View Logs" shows up for an operator with ActionLogs.view
 * even if they have no edit rights on the user record.
 */
function UserProfileActions(props: UserProfileActionsProps) {
  const { canEdit, editor, item, onInputOpen, show = {} } = props;
  const navigate = useNavigate();
  const permissions = useSelector(authorizeSelectors.permissions);

  // Audit fix 4 — role flip is admin-only. We piggy-back on the Admins
  // permission (same operators manage both). Using any related capability is
  // fine; the server also enforces [UserHasAnyRole(AppAdmin)].
  const canFlipCompanyAdmin = permissions.can("PlatformAdmins", "edit");

  async function flipCompanyAdmin(promote: boolean): Promise<void> {
    if (!item?.uid) return;
    Modal.confirm({
      title: promote ? "Promote to Company Admin?" : "Demote from Company Admin?",
      content: promote
        ? "The user will gain company-wide admin rights. They remain a company user."
        : "The user will lose the Company Admin role but stay as a regular company user.",
      okText: promote ? "Promote" : "Demote",
      okButtonProps: promote ? undefined : { danger: true },
      onOk: async () => {
        try {
          if (promote) await promoteToCompanyAdmin(item.uid);
          else await demoteFromCompanyAdmin(item.uid);
          message.success(promote ? "User is now a Company Admin." : "Company Admin role removed.");
          props.onReload?.();
        } catch (err) {
          message.error(getProblemMessage(err) || "Failed to change role.");
        }
      },
    });
  }

  // Each action gates on its OWN permission. Previously the whole dropdown was hidden when canEdit was
  // false, which incorrectly suppressed View Logs / Schedule Event / Assign Reward for operators who
  // were legitimately allowed to perform those but not to edit the user record itself.
  const canViewLogs = permissions.can("ActionLogs", "view");
  const canScheduleEvent = permissions.can("CalendarEvents", "create");
  const canAssignReward = permissions.can("RewardsAssignment", "create");
  // Guard every item against a half-loaded card — if the card is still fetching, `item` is null and
  // `item.uid` is undefined; firing an action with an undefined uid produces a confusing 400 later.
  const hasUid = !!item?.uid;

  if (editor.editing) {
    return (
      <Space className="flex flex-row">
        <EditModeActions editor={editor} />
      </Space>
    );
  }

  const items: MenuProps["items"] = [];

  if (show.assignReward && canAssignReward) {
    items.push({
      key: "assignReward",
      label: "Assign Reward",
      disabled: !hasUid,
      onClick: () => onInputOpen("assignReward"),
    });
  }

  if (show.confirmContacts && canEdit) {
    items.push(
      {
        key: "confirmEmail",
        // Sends the user a verification request (does not mark it confirmed) — label reflects that.
        label: "Request Email Confirmation",
        disabled: !hasUid || item?.emailConfirmed || !item?.email,
        onClick: () => onInputOpen("confirmEmail"),
      },
      {
        key: "confirmPhone",
        label: "Request Phone Confirmation",
        disabled: !hasUid || item?.phoneNumberConfirmed || !item?.phoneNumber,
        onClick: () => onInputOpen("confirmPhone"),
      },
      {
        key: "confirmCompanyPhone",
        label: "Request Company-Phone Confirmation",
        disabled: !hasUid || item?.companyPhoneNumberConfirmed || !item?.companyPhoneNumber,
        onClick: () => onInputOpen("confirmCompanyPhone"),
      },
    );
  }

  // PD-01 §4.3 lists "view logs" among the actions on a user. The uid travels on the URL query now, so
  // the filter survives a hard refresh of /system-log/action-logs or a shared link.
  if (canViewLogs && hasUid) {
    items.push({
      key: "viewLogs",
      label: "View Logs",
      onClick: () => {
        const userName =
          item?.userName ??
          `${item?.firstName ?? ""} ${item?.lastName ?? ""}`.trim();
        const params = new URLSearchParams({ userUid: item!.uid });
        if (userName) params.set("userName", userName);
        navigate(`/system-log/action-logs?${params.toString()}`);
      },
    });
  }

  if (canEdit) {
    items.push({
      key: "edit",
      label: "Edit",
      disabled: !hasUid,
      onClick: () => editor.startEditing(),
    });
  }

  if (canEdit) {
    const noEmail = !item?.email;
    items.push({
      key: "resetPassword",
      // R5: explain WHY the item is disabled — "no email" is a configuration gap, not a bug the operator
      // should guess at. The tooltip only wraps the label when disabled; the active state stays plain.
      label: noEmail ? (
        <Tooltip title="This user has no email on file — add one before sending a password reset.">
          <span>Reset Password</span>
        </Tooltip>
      ) : (
        "Reset Password"
      ),
      danger: true,
      disabled: !hasUid || noEmail,
      onClick: () => onInputOpen("resetPassword"),
    });
  }

  // Audit fix 4 — Promote / Demote Company Admin. Visible only when the host
  // page opts in (Users page), the caller has admin privileges, and the user
  // is a company user (we rely on the roleName / platformRoleName fields in
  // `item`; absence of the field means "don't offer the action").
  if (show.companyAdminToggle && canFlipCompanyAdmin && hasUid) {
    const roleNames = String(item?.platformRoleName ?? item?.roleName ?? "").toLowerCase();
    const isAlreadyCompanyAdmin = roleNames.includes("companyadmin");
    const isCompanyUser = roleNames.includes("companyuser") || roleNames.includes("companyadmin");
    if (isCompanyUser) {
      items.push({
        key: isAlreadyCompanyAdmin ? "demoteCompanyAdmin" : "promoteCompanyAdmin",
        label: isAlreadyCompanyAdmin ? "Demote from Company Admin" : "Promote to Company Admin",
        danger: isAlreadyCompanyAdmin,
        onClick: () => flipCompanyAdmin(!isAlreadyCompanyAdmin),
      });
    }
  }

  if (show.lock && canEdit) {
    items.push({
      key: "lockUser",
      label: item?.lockoutEnd ? "Unlock User" : "Lock User",
      danger: true,
      disabled: !hasUid,
      onClick: () => onInputOpen(item?.lockoutEnd ? "unlock" : "lock"),
    });
  }

  // Degrade gracefully. If there's nothing the operator is allowed to do here, don't render a disabled
  // primary button that says "Schedule Event" — show an empty header. If they can't schedule but have
  // other actions, show a plain dropdown without the misleading primary.
  if (!canScheduleEvent && items.length === 0) {
    return <Space className="flex flex-row" />;
  }

  if (!canScheduleEvent) {
    return (
      <Space className="flex flex-row">
        <Dropdown menu={{ items }}>
          <Button>Actions</Button>
        </Dropdown>
      </Space>
    );
  }

  return (
    <Space className="flex flex-row">
      <Dropdown.Button
        type="primary"
        disabled={!hasUid}
        menu={{ items }}
        onClick={() => onInputOpen("scheduleEvent")}
      >
        Schedule Event
      </Dropdown.Button>
    </Space>
  );
}

export default UserProfileActions;
